import {
  Component,
  DestroyRef,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Observable } from 'rxjs';
import { IClassWithCurrUserResModel } from '../../models/classWithCurrUserRes.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClassService } from '../../services/class.service';
import { map } from 'rxjs/operators';
import { ReservationService } from '../../services/reservation.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { ErrorMessageUtils } from '../../utils/ErrorMessageUtils';
import { ToastService } from '../../ui/overlay/toast.service';

@Component({
  selector: 'app-my-reservations',
  templateUrl: './my-reservations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MyReservationsComponent {
  isFull(spinClass: {
    currentReservations: number;
    maxReservations: number;
  }): boolean {
    return spinClass.currentReservations >= spinClass.maxReservations;
  }

  private readonly toast = inject(ToastService);
  private readonly classService = inject(ClassService);

  /**
   * The template subscribes to this stream itself. Feeding a plain Subject from a
   * constructor subscription instead loses the first emission whenever the class
   * listener is already warm: it replays synchronously while the view does not exist
   * yet, so the async pipe attaches to a Subject that has already fired, and a member
   * with bookings sees "Nog geen reservaties" until something else re-emits.
   */
  readonly classes$: Observable<IClassWithCurrUserResModel[]> =
    this.classService
      .getClassesWithReservationsForCurrentUser()
      .pipe(
        map((classes) =>
          classes.filter((c) => c.currentReservationsForUser > 0),
        ),
      );

  inSubmission: boolean = false;
  currentUserId: string = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private destroyerRef: DestroyRef,
    private reservationService: ReservationService,
  ) {
    this.authService.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.currentUserId = user?.id ?? '';
    });
  }

  removeReservation(
    spinClass: IClassWithCurrUserResModel,
    isDisabled: boolean,
  ): void {
    if (this.currentUserId.length > 0 && !isDisabled) {
      this.inSubmission = true;
      this.reservationService
        .removeReservation(this.currentUserId, spinClass.id)
        .then(() => {
          // The row must go with the toast, not with the next snapshot: see
          // ClassService.overrideOwnReservations.
          this.classService.overrideOwnReservations(
            spinClass.id,
            spinClass.currentReservationsForUser - 1,
          );
          // The undo re-books rather than rolling back: there is no transaction to
          // reverse, and on a full class the seat may already be gone, in which case
          // addReservation fails with the normal message. That is the honest outcome.
          this.toast.success('Fiets geannuleerd.', {
            label: 'Ongedaan maken',
            run: () => this.restoreReservation(spinClass),
          });
        })
        .catch((error) => {
          this.toast.error(
            ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
          );
        })
        .finally(() => {
          this.inSubmission = false;
        });
    }
  }

  private restoreReservation(spinClass: IClassWithCurrUserResModel): void {
    this.reservationService
      .addReservation(this.currentUserId, spinClass.id)
      .then(() => {
        this.classService.overrideOwnReservations(
          spinClass.id,
          spinClass.currentReservationsForUser,
        );
        this.toast.success('Fiets terug gereserveerd.');
      })
      .catch((error) =>
        this.toast.error(
          ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
        ),
      );
  }

  navigateToReservePage() {
    this.router.navigate(['/reserveer']);
  }
}
